// iter-297 — Universal Payment Gateway.
//
// Reusable payment entry page any flow in the platform can navigate to:
//
//   /pay-gateway?amount=1500&title=شراء منتج&reference=ORD-123&return_url=/orders/ORD-123
//
// Query params:
//   amount          (required, decimal AED)
//   title           display title (optional)
//   description     secondary line (optional)
//   reference       anything the merchant wants echoed back (optional)
//   return_url      where to send the user after a successful wallet payment
//                   (defaults to /payments)
//
// Also supports internal `state`-based navigation:
//   navigate('/pay-gateway', { state: { amount, title, ... } })
//
// Behaviour:
//   • Fetches /api/payment-methods + /api/agent/earnings-summary in parallel
//     with Promise.allSettled so one slow hop never 522s the page.
//   • Wallet card is ALWAYS visible with the live balance (shows "insufficient"
//     when < amount but still renders — so the user never thinks the gateway
//     is empty).
//   • Picks a provider → POST /api/payments/create-link → redirect.
//   • Picks wallet → POST /api/payments/wallet-charge (new generic endpoint).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, ArrowLeft, AlertCircle, ShieldCheck, Wallet, CheckCircle2, CreditCard,
  RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentGateway() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  // Merge query params + navigation state (state wins) so this page is
  // callable both via a deep link and via navigate(..., {state}).
  const payload = useMemo(() => ({
    amount: Number(location.state?.amount ?? searchParams.get('amount') ?? 0),
    title: location.state?.title || searchParams.get('title') || '',
    description: location.state?.description || searchParams.get('description') || '',
    reference: location.state?.reference || searchParams.get('reference') || '',
    purpose: location.state?.purpose || searchParams.get('purpose') || 'misc',
    return_url: location.state?.return_url || searchParams.get('return_url') || '/payments',
  }), [location.state, searchParams]);

  const [providers, setProviders] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [creatingFor, setCreatingFor] = useState(null);

  const token = () => localStorage.getItem('business_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setLoading(true);
    setRefreshing(true);
    setError('');
    try {
      const [methodsRes, earningsRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/payment-methods`, { headers: authHeader(), timeout: 15000 }),
        axios.get(`${BACKEND_URL}/api/agent/earnings-summary`, { headers: authHeader(), timeout: 15000 }),
      ]);
      if (methodsRes.status === 'fulfilled') {
        setProviders(methodsRes.value.data?.providers || []);
      } else {
        setError(language === 'ar' ? 'تعذّر تحميل بوابات الدفع' : 'Could not load payment gateways');
      }
      if (earningsRes.status === 'fulfilled') {
        const bal = Number(earningsRes.value.data?.available_balance ?? 0);
        setWalletBalance(bal < 0 ? 0 : bal);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [language]);

  useEffect(() => { if (token()) load(); /* eslint-disable-next-line */ }, []);

  /* ─── Checkout handlers ─── */

  const pickWallet = async () => {
    const amtDue = payload.amount;
    const confirmMsg = language === 'ar'
      ? `سيُخصم ${amtDue.toFixed(2)} درهم من محفظتك. موافق؟`
      : `${amtDue.toFixed(2)} AED will be deducted from your wallet. Continue?`;
    if (!window.confirm(confirmMsg)) return;

    setCreatingFor('wallet');
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/payments/wallet-charge`,
        {
          amount: payload.amount,
          reference: payload.reference,
          purpose: payload.purpose,
          description: payload.title || payload.description || 'Payment',
        },
        { headers: authHeader(), timeout: 20000 }
      );
      const newBal = Number(r.data?.new_balance ?? 0);
      toast.success(language === 'ar'
        ? `تم الدفع. الرصيد المتبقي: ${newBal.toFixed(2)} درهم`
        : `Paid. New balance: AED ${newBal.toFixed(2)}`);
      // Return to origin
      if (payload.return_url?.startsWith('http')) {
        window.location.href = payload.return_url;
      } else {
        navigate(payload.return_url || '/payments');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('INSUFFICIENT_WALLET|')) {
        const [, avail, need] = detail.split('|');
        toast.error(language === 'ar'
          ? `الرصيد غير كافٍ (متاح ${avail}، مطلوب ${need})`
          : `Insufficient balance (available ${avail}, need ${need})`);
      } else {
        toast.error(typeof detail === 'string' ? detail : (e.message || 'Wallet payment failed'));
      }
      setCreatingFor(null);
    }
  };

  const pickProvider = async (provider) => {
    setCreatingFor(provider.provider);
    try {
      // iter-309 — Pass OUR origin for return URLs so the hub never
      // bounces the user back to the admin dashboard on cancel/success.
      const origin = window.location.origin;
      const ref = payload.reference || `cgb-${Date.now()}`;
      const r = await axios.post(
        `${BACKEND_URL}/api/payments/create-link`,
        {
          provider: provider.provider,
          amount: payload.amount,
          currency: 'AED',
          description: payload.title || payload.description || 'Payment',
          reference: ref,
          success_url: `${origin}/payment-success?inv=${encodeURIComponent(ref)}`,
          cancel_url: `${origin}/payment-cancelled?inv=${encodeURIComponent(ref)}&reason=user_cancelled&provider=${encodeURIComponent(provider.provider)}`,
        },
        { headers: authHeader(), timeout: 20000 }
      );
      const url = r.data?.checkout_url;
      if (!url) throw new Error('Missing checkout_url');
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Could not create payment link');
      setCreatingFor(null);
    }
  };

  /* ─── Guard: no amount? ─── */
  if (!payload.amount || payload.amount <= 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 cgm-page`}>
        <div className="max-w-sm text-center">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-2">
            {language === 'ar' ? 'لا يوجد مبلغ محدد' : 'No amount specified'}
          </h2>
          <p className="text-sm opacity-70 mb-4">
            {language === 'ar'
              ? 'لا يمكن فتح بوابة الدفع بدون مبلغ. ارجع واختر الفاتورة أو المنتج المراد تسديده.'
              : 'Cannot open the payment gateway without an amount. Go back and pick an invoice / product.'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-[#1E9BD9] text-white font-semibold"
            data-testid="pay-no-amount-back"
          >
            {language === 'ar' ? 'رجوع' : 'Back'}
          </button>
        </div>
      </div>
    );
  }

  const canWalletPay = walletBalance >= payload.amount;
  const isRTL = language === 'ar';

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`min-h-screen cgm-page`}
      data-testid="payment-gateway"
    >
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="pay-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'ar' ? 'رجوع' : 'Back'}
          </button>
          <div className="flex items-center gap-2 text-xs opacity-70">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            {language === 'ar' ? 'تشفير SSL آمن' : 'Secure SSL'}
          </div>
        </div>

        {/* Amount card */}
        <div
          className={`rounded-2xl p-6 mb-5 ${
            isDarkMode
              ? 'bg-gradient-to-br from-[#131B2C] to-[#0F1524] border border-white/10'
              : 'bg-white shadow-xl border border-black/5'
          }`}
          data-testid="pay-amount-card"
        >
          <div className="text-xs uppercase tracking-wide opacity-60 mb-1">
            {language === 'ar' ? 'المبلغ المطلوب' : 'Amount due'}
          </div>
          <div className="text-4xl sm:text-5xl font-extrabold mb-2" data-testid="pay-amount-value">
            AED {payload.amount.toFixed(2)}
          </div>
          {payload.title && (
            <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {payload.title}
            </div>
          )}
          {payload.description && (
            <div className={`text-xs opacity-70 mt-1`}>{payload.description}</div>
          )}
          {payload.reference && (
            <div className="mt-2 font-mono text-[11px] opacity-50">#{payload.reference}</div>
          )}
        </div>

        {/* Methods header */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">
            {language === 'ar' ? 'اختر طريقة الدفع' : 'Choose payment method'}
          </h2>
          <button
            onClick={load}
            disabled={refreshing}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}
            data-testid="pay-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </button>
        </div>

        {loading && (
          <div className="py-10 flex justify-center" data-testid="pay-loading">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        )}

        {!loading && (
          <>
            {/* Wallet card — always visible with live balance */}
            <button
              type="button"
              onClick={pickWallet}
              disabled={!canWalletPay || creatingFor !== null}
              data-testid="provider-wallet"
              className={`w-full text-start p-4 rounded-xl border mb-3 transition-all ${
                canWalletPay && creatingFor === null
                  ? isDarkMode
                    ? 'bg-emerald-500/10 border-emerald-500/40 hover:bg-emerald-500/15'
                    : 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                  : isDarkMode
                    ? 'bg-white/5 border-white/10 opacity-60'
                    : 'bg-gray-100 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg,#10B981 0%,#059669 100%)' }}>
                  <Wallet className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {language === 'ar' ? 'المحفظة' : 'Wallet'}
                    {canWalletPay && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/30 text-emerald-700 dark:text-emerald-200">
                        {language === 'ar' ? 'فوري' : 'Instant'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5 flex items-center gap-1.5 opacity-80">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    {language === 'ar' ? 'الرصيد المتاح:' : 'Available:'}
                    <span className={`font-semibold ${canWalletPay ? 'text-emerald-600' : 'text-rose-500'}`}>
                      AED {walletBalance.toFixed(2)}
                    </span>
                  </div>
                  {!canWalletPay && (
                    <div className="text-[10px] mt-1 text-rose-500" data-testid="wallet-insufficient">
                      {language === 'ar'
                        ? `الرصيد غير كافٍ (المطلوب AED ${payload.amount.toFixed(2)})`
                        : `Insufficient (need AED ${payload.amount.toFixed(2)})`}
                    </div>
                  )}
                </div>
                {creatingFor === 'wallet' && <Loader2 className="h-4 w-4 animate-spin opacity-60" />}
              </div>
            </button>

            {/* Providers */}
            {providers.length === 0 ? (
              <div
                className={`p-6 rounded-xl border text-center ${isDarkMode ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                data-testid="pay-no-providers"
              >
                <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">
                  {language === 'ar'
                    ? 'لا توجد بوابات دفع خارجية مفعّلة حالياً'
                    : 'No external payment gateways enabled right now'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="providers-grid">
                {providers.map((p) => {
                  const busy = creatingFor === p.provider;
                  const disabled = creatingFor !== null && !busy;
                  const belowMin = payload.amount < (Number(p.min_amount) || 0);
                  const aboveMax = p.max_amount ? payload.amount > Number(p.max_amount) : false;
                  const clickable = !busy && !disabled && !belowMin && !aboveMax;
                  return (
                    <button
                      key={p.provider}
                      disabled={!clickable}
                      onClick={() => pickProvider(p)}
                      data-testid={`provider-${p.provider}`}
                      className={`text-start p-4 rounded-xl border transition-all ${
                        clickable
                          ? isDarkMode
                            ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-blue-400/50'
                            : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'
                          : 'opacity-50 cursor-not-allowed'
                      } ${busy ? 'cursor-wait' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        {p.icon_url ? (
                          <img src={p.icon_url} alt={p.provider} className="w-12 h-12 object-contain rounded-lg bg-white p-1" />
                        ) : (
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-blue-500 to-indigo-600`}>
                            <CreditCard className="h-6 w-6" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {language === 'ar' ? (p.display_name_ar || p.provider) : (p.display_name_en || p.provider)}
                          </div>
                          <div className="text-[11px] opacity-70 truncate">
                            {language === 'ar' ? (p.description_ar || '') : (p.description_en || '')}
                          </div>
                          {(belowMin || aboveMax) && (
                            <div className="text-[10px] text-rose-500 mt-1">
                              {belowMin
                                ? (language === 'ar' ? `الحد الأدنى AED ${p.min_amount}` : `Min AED ${p.min_amount}`)
                                : (language === 'ar' ? `الحد الأعلى AED ${p.max_amount}` : `Max AED ${p.max_amount}`)}
                            </div>
                          )}
                        </div>
                        {busy && <Loader2 className="h-4 w-4 animate-spin opacity-60" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {error && (
              <p className="mt-4 text-sm text-rose-500 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </p>
            )}
          </>
        )}

        <div className={`mt-6 text-[11px] text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {language === 'ar'
            ? 'جميع المدفوعات محمية بتشفير SSL · البوابات مُدارة من ASB606 Admin'
            : 'All payments SSL-encrypted · Gateways managed by ASB606 Admin'}
        </div>
      </div>
    </div>
  );
}
